import { numberParam, stringParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import {
  describeOfficial,
  judgeRule,
  missingRequirementReason,
  notChecked,
  plainResult,
  withRequirement,
} from './support.js';

/**
 * Eye line check.
 *
 * Authorities state eye height from the *bottom* edge ("2/3 up the photo"),
 * while the overlay draws a horizontal line at the measured eye level. Both
 * representations are produced here so the rule engine and the overlay cannot
 * drift apart.
 */
export const eyeLineCheck: ValidationCheck = {
  id: 'eye_line',
  title: 'Положение глаз',
  description: 'Высота линии глаз относительно нижнего края снимка.',
  kind: 'geometry',
  requires: ['geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['geometry']);
    if (missing) return notChecked(ctx, eyeLineCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    if (!geometry) return notChecked(ctx, eyeLineCheck, 'Геометрия недоступна.', ctx.rule.severity);

    const reference = stringParam(ctx.rule.params, 'reference', 'bottom');
    const tolerance = numberParam(ctx.rule.params, 'tolerance', ctx.rule.measurement?.tolerance ?? 0.01);
    const confidence = geometry.faceBoxConfidence.value;

    const value = reference === 'bottom' ? geometry.eyeLineFromBottom : geometry.eyeLineFromTop;
    const { verdict, bounds } = judgeRule(ctx.rule, value, { confidence, warningMargin: 0.07 });

    const officialText = describeOfficial(bounds.official) ?? '—';
    const actual = `${(value * 100).toFixed(1).replace('.', ',')} %`;
    const fromBottom = reference === 'bottom';
    const side = fromBottom ? 'нижнего' : 'верхнего';

    const message = (() => {
      if (verdict.status === 'uncertain' && verdict.reason === 'low_confidence') {
        return `Положение глаз определено неуверенно: около ${actual} от ${side} края.`;
      }
      if (verdict.reason === 'within_tolerance') {
        return `Линия глаз ${actual} от ${side} края — на границе официального диапазона ${officialText}, разница в пределах погрешности измерения.`;
      }
      if (verdict.reason === 'policy_below_min' || verdict.reason === 'policy_above_max') {
        return `Линия глаз ${actual} — внутри официальных ${officialText}, но выходит за наш внутренний ориентир.`;
      }
      if (verdict.reason === 'above_max') {
        return fromBottom
          ? `Глаза расположены слишком высоко: ${actual} от нижнего края, официальный диапазон — ${officialText}.`
          : `Глаза расположены слишком низко: ${actual} от верхнего края, официальный диапазон — ${officialText}.`;
      }
      if (verdict.reason === 'below_min') {
        return fromBottom
          ? `Глаза расположены слишком низко: ${actual} от нижнего края, официальный диапазон — ${officialText}.`
          : `Глаза расположены слишком высоко: ${actual} от верхнего края, официальный диапазон — ${officialText}.`;
      }
      if (verdict.status === 'warning') {
        return `Линия глаз ${actual} — близко к границе официального диапазона ${officialText}.`;
      }
      return `Линия глаз на ${actual} от ${side} края — в официальном диапазоне ${officialText}.`;
    })();

    const eyeLineY = roundTo(geometry.eyeLineFromTop, 4);
    // Allowed band in image coordinates (y from the top, as everything else).
    const bandMin = bounds.effective.min ?? 0;
    const bandMax = bounds.effective.max ?? 1;
    const bandTop = fromBottom ? roundTo(1 - bandMax, 4) : roundTo(bandMin, 4);
    const bandBottom = fromBottom ? roundTo(1 - bandMin, 4) : roundTo(bandMax, 4);

    const shapes: NonNullable<CheckResult['geometry']> = [
      { type: 'rect', coordinates: [0, bandTop, 1, roundTo(bandBottom - bandTop, 4)] },
      { type: 'line', coordinates: [0, eyeLineY, 1, eyeLineY] },
      { type: 'point', coordinates: [roundTo(geometry.eyeLeft.x, 4), roundTo(geometry.eyeLeft.y, 4)] },
      { type: 'point', coordinates: [roundTo(geometry.eyeRight.x, 4), roundTo(geometry.eyeRight.y, 4)] },
    ];

    const result = plainResult({
      id: eyeLineCheck.id,
      title: eyeLineCheck.title,
      status: verdict.status,
      severity: ctx.rule.severity,
      value: roundTo(value, 4),
      ...(bounds.official.min !== undefined ? { min: bounds.official.min } : {}),
      ...(bounds.official.max !== undefined ? { max: bounds.official.max } : {}),
      unit: 'ratio',
      message,
      requirement:
        describeOfficial(bounds.official) !== undefined
          ? `Официально: положение глаз ${officialText} высоты изображения, считая от ${side} края.`
          : undefined,
      fixability: 'fixable',
      geometry: shapes,
      confidence: roundTo(confidence, 2),
      details: {
        eyeLineFromTop: geometry.eyeLineFromTop,
        eyeLineFromBottom: geometry.eyeLineFromBottom,
        reference,
        allowedBand: { top: bandTop, bottom: bandBottom },
        verdictReason: verdict.reason ?? 'ok',
        policyViolation: verdict.policyViolation === true,
        tolerance,
        displayValue: actual,
      },
    });

    return withRequirement(result, ctx.rule, { reference, unit: 'ratio', tolerance });
  },
};
